// Campaign videos and drafts with the AI review for each: live progress while
// a run is going, then the verdict, issue count and result mix.
import React from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InstagramIcon from '@mui/icons-material/Instagram';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import YouTubeIcon from '@mui/icons-material/YouTube';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import { STATUS, VERDICT, fmtDateTime, isRunning } from './qcFormat';
import { asText } from './qcClient';
import { IssueSummary } from './IssueStrip';
import { ScanCompact } from './ScanProgress';
import { Button, C, Pill, VerdictBadge } from './ui';

const HEADERS = [
    { label: 'Video', align: 'left' },
    { label: 'AI review', align: 'left', width: 220 },
    { label: 'Issues', align: 'left', width: 120 },
    { label: 'Actions', align: 'right', width: 210 },
];

const TILES = {
    YouTube: { Icon: YouTubeIcon, bg: '#fff1f2', color: '#e11d48' },
    Instagram: { Icon: InstagramIcon, bg: '#fdf2f8', color: '#c026d3' },
    draft: { Icon: UploadFileOutlinedIcon, bg: '#eef2ff', color: '#4338ca' },
    other: { Icon: OndemandVideoOutlinedIcon, bg: C.soft, color: C.muted },
};

// A real frame when the API sends one; otherwise the platform's icon.
function Thumb({ row }) {
    const frame = (row.qc?.frames || []).find((f) => typeof f?.url === 'string');
    if (frame) return <img src={frame.url} alt="" style={{ width: 40, height: 52, objectFit: 'cover', borderRadius: 9, flexShrink: 0, background: C.soft }} />;
    const tile = TILES[row.isDraft ? 'draft' : row.platform] || TILES.other;
    return (
        <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: 10, background: tile.bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <tile.Icon style={{ fontSize: 21, color: tile.color }} />
        </span>
    );
}

function VideoCell({ row }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <Thumb row={row} />
            <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13.5, color: C.text, lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }} title={row.title}>{row.title}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 3, display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
                    {row.isDraft && <Pill bg="#eef2ff" color="#4338ca">Draft</Pill>}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.isDraft ? 'Not linked to a deal' : row.subtitle}</span>
                </div>
            </div>
        </div>
    );
}

function CheckedBy({ check }) {
    const human = check.kind === 'human';
    const Icon = human ? PersonOutlineRoundedIcon : AutoAwesomeIcon;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.muted, marginTop: 5, whiteSpace: 'nowrap' }}>
            <Icon aria-hidden="true" style={{ fontSize: 13, color: human ? C.navy700 : '#c79309' }} />
            <span>{human ? 'Human reviewed' : 'AI check'} · {fmtDateTime(check.created_at)}</span>
        </div>
    );
}

// A failed run's message, kept to three lines (long service errors carry
// unbroken IDs); the full text is on hover and in Details.
const ERROR_TEXT = {
    fontSize: 12, color: '#991b1b', marginTop: 5, maxWidth: 200, lineHeight: 1.35, overflowWrap: 'anywhere',
    overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
};

function ReviewCell({ qc }) {
    const status = qc?.status || 'pending';
    if (isRunning(status)) return <ScanCompact status={status} stage={qc.stage} />;
    if (status === 'error') {
        return (
            <div>
                <Pill bg={STATUS.error.bg} color={STATUS.error.color}>{STATUS.error.label}</Pill>
                {asText(qc.error) && <div title={asText(qc.error)} style={ERROR_TEXT}>{asText(qc.error)}</div>}
            </div>
        );
    }
    const check = qc?.latest_check;
    if (check && VERDICT[check.verdict]) return <div><VerdictBadge verdict={check.verdict} /><CheckedBy check={check} /></div>;
    const s = STATUS[status] || STATUS.pending;
    return <Pill bg={s.bg} color={s.color}>{s.label}</Pill>;
}

function RowActions({ row, canRun, starting, onRun, onOpen }) {
    const qc = row.qc;
    const running = Boolean(qc) && isRunning(qc.status);
    const runLabel = qc && qc.status !== 'pending' ? 'Re-run' : 'Run QC';
    const runTitle = !row.source ? 'This video has no link to check' : !canRun ? 'Add a checklist first' : undefined;
    let open = null;
    if (running) open = <Button small onClick={() => onOpen(row)}><VisibilityOutlinedIcon aria-hidden="true" style={{ fontSize: 15 }} />Live view</Button>;
    else if (qc?.latest_check) open = <Button small onClick={() => onOpen(row)}>View result</Button>;
    else if (qc?.status === 'error') open = <Button small onClick={() => onOpen(row)}>Details</Button>;
    const firstRun = runLabel === 'Run QC';
    return (
        <div style={{ display: 'inline-flex', gap: 8, justifyContent: 'flex-end' }}>
            {open}
            <Button small variant={firstRun ? 'primary' : 'secondary'} title={runTitle} disabled={!row.source || !canRun || running || starting} onClick={() => onRun(row)}>
                {firstRun ? <AutoAwesomeIcon aria-hidden="true" style={{ fontSize: 14 }} /> : <ReplayRoundedIcon aria-hidden="true" style={{ fontSize: 15 }} />}
                {starting ? 'Starting…' : runLabel}
            </Button>
        </div>
    );
}

// Left edge of each row in its verdict colour, so problems stand out in a long list.
const edgeFor = (qc) => {
    if (qc && isRunning(qc.status)) return C.gold;
    return VERDICT[qc?.latest_check?.verdict]?.color || 'transparent';
};

const cell = { padding: '13px 16px', verticalAlign: 'middle' };

export default function QcVideoTable({ rows, canRun, startingKey, onRun, onOpen }) {
    return (
        <div style={{ border: `1px solid ${C.line}`, borderRadius: 12, overflowX: 'auto', background: '#fff' }}>
            <table className="vqc-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                <thead>
                    <tr style={{ background: C.head }}>
                        {HEADERS.map((h) => (
                            <th key={h.label} style={{ padding: '11px 16px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, color: C.th, textTransform: 'uppercase', textAlign: h.align, width: h.width, whiteSpace: 'nowrap', borderBottom: `1px solid ${C.line}` }}>{h.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key} className="vqc-row" style={{ borderTop: `1px solid ${C.line}` }}>
                            <td style={{ ...cell, maxWidth: 320, boxShadow: `inset 3px 0 0 ${edgeFor(row.qc)}` }}><VideoCell row={row} /></td>
                            <td style={cell}><ReviewCell qc={row.qc} /></td>
                            <td style={cell}><IssueSummary latest={row.qc?.latest_check} /></td>
                            <td style={{ ...cell, textAlign: 'right' }}>
                                <RowActions row={row} canRun={canRun} starting={startingKey === row.key} onRun={onRun} onOpen={onOpen} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
